import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { DroneManufacturer } from '../../core/data-models/drone-manufacturer.model';
import { DroneModel } from '../../core/data-models/drone-model.model';
import { DroneStatus } from '../../core/data-models/drone-status.model';
import { canChangeDroneStatus } from '../../shared/utils/drone-status.utils';
import { Drone } from '../../core/data-models/drone.model';
import { DroneManufacturerService } from '../../core/data-services/drone-manufacturer.service';
import { DroneModelService } from '../../core/data-services/drone-model.service';
import { DroneService } from '../../core/data-services/drone.service';
import { DroneRequest } from '../../core/request-models/drone-request.model';
import { optionalText } from '../../core/utils/form-value-utils';

@Component({
  selector: 'app-drone-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './drone-form.component.html',
  styleUrl: './drone-form.component.scss'
})
export class DroneFormComponent implements OnInit {
  droneForm: FormGroup;
  loading = false;
  loadingDrone = false;
  errorMessage = '';
  successMessage = '';

  isEditMode = false;
  droneId: string | null = null;

  // Dropdown options from API
  manufacturers: DroneManufacturer[] = [];
  allModels: DroneModel[] = [];
  availableModels: DroneModel[] = [];

  /** The status the drone had when loaded; a change goes through PUT /status, not the drone PUT. */
  private loadedStatus: DroneStatus | null = null;

  /**
   * A leased drone shows 'leased' and can only be put into maintenance here (`canChangeDroneStatus`):
   * the rest comes from its lease (Edit Lease) or ending the assignment, and the status route rejects 'leased'.
   */
  get isLeased(): boolean {
    return this.loadedStatus === 'leased';
  }

  statuses = [
    { value: 'available', label: 'Available' },
    { value: 'assigned', label: 'Assigned' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'retired', label: 'Retired' }
  ];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private droneService: DroneService,
    private droneManufacturerService: DroneManufacturerService,
    private droneModelService: DroneModelService
  ) {
    this.droneForm = this.fb.group({
      name: ['', Validators.required],
      manufacturerId: ['', Validators.required],
      modelId: ['', Validators.required],
      serialNumber: ['', Validators.required],
      packagingSerialNumber: [''],
      version: [''],
      status: ['available', Validators.required],
      inventoryNote: [''],
      // Registration fields
      nNumber: [''],
      nNumberActiveDate: [''],
      remoteId: [''],
      remoteBroadcastModule: [''],
    });

    // Watch manufacturer changes to update available models
    this.droneForm.get('manufacturerId')?.valueChanges.subscribe(manufacturerId => {
      this.updateAvailableModels(manufacturerId);
    });
  }

  ngOnInit(): void {
    // Check if we're in edit mode
    this.droneId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!this.droneId && this.droneId !== 'create';

    // Load manufacturers and models first
    this.loadManufacturersAndModels().then(() => {
      if (this.isEditMode) {
        this.loadDrone();
      }
    });
  }

  async loadManufacturersAndModels(): Promise<void> {
    try {
      [this.manufacturers, this.allModels] = await Promise.all([
        firstValueFrom(this.droneManufacturerService.getManufacturers()),
        firstValueFrom(this.droneModelService.getModels()),
      ]);
    } catch (error) {
      console.error('Failed to load manufacturers/models:', error);
      this.errorMessage = 'Failed to load manufacturer and model options';
    }
  }

  updateAvailableModels(manufacturerId: string | null | undefined): void {
    if (!manufacturerId) {
      this.availableModels = [];
      return;
    }

    this.availableModels = this.allModels.filter(m => m.manufacturerId === manufacturerId);

    // Reset model if current model isn't in the new list
    const currentModelId = this.droneForm.get('modelId')?.value;
    if (currentModelId && !this.availableModels.find(m => m.id === currentModelId)) {
      this.droneForm.get('modelId')?.setValue('');
    }
  }

  async loadDrone(): Promise<void> {
    if (!this.droneId) return;
    this.loadingDrone = true;

    try {
      const drone = await firstValueFrom(this.droneService.getDroneById(this.droneId));
      if (drone) {
        this.patchForm(drone);
      }
    } catch (error) {
      console.error('Failed to load drone:', error);
      this.errorMessage = 'Failed to load drone details';
    } finally {
      this.loadingDrone = false;
    }
  }

  private patchForm(drone: Drone): void {
    // Update available models for this manufacturer first
    this.updateAvailableModels(drone.manufacturerId);
    this.loadedStatus = drone.status ?? null;

    // Format dates for input fields
    const nNumberActiveDate = drone.nNumberActiveDate
      ? new Date(drone.nNumberActiveDate).toISOString().split('T')[0]
      : '';

    this.droneForm.patchValue({
      name: drone.name ?? '',
      manufacturerId: drone.manufacturerId ?? '',
      modelId: drone.modelId ?? '',
      serialNumber: drone.serialNumber ?? '',
      packagingSerialNumber: drone.packagingSerialNumber ?? '',
      version: drone.version ?? '',
      status: drone.status ?? 'available',
      inventoryNote: drone.inventoryNote ?? '',
      nNumber: drone.nNumber ?? '',
      nNumberActiveDate: nNumberActiveDate,
      remoteId: drone.remoteId ?? '',
      remoteBroadcastModule: drone.remoteBroadcastModule ?? '',
    });
  }

  async onSubmit(): Promise<void> {
    if (this.droneForm.invalid) {
      this.droneForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      if (this.isEditMode && this.droneId) {
        await this.saveChanges(this.droneId);
      } else {
        await this.createAndShowSuccess();
      }
    } catch (error) {
      console.error('Save drone error:', error);
      this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? (this.isEditMode
        ? 'Failed to update drone. Please try again.'
        : 'Failed to create drone. Please try again.');
    } finally {
      this.loading = false;
    }
  }

  /** The form as the hub's writable drone fields; emptied optional fields become `null` (clear). */
  private toRequest(): DroneRequest {
    const formData = this.droneForm.value;
    return {
      name: formData.name,
      manufacturerId: formData.manufacturerId,
      modelId: formData.modelId,
      serialNumber: formData.serialNumber,
      packagingSerialNumber: optionalText(formData.packagingSerialNumber),
      version: optionalText(formData.version),
      inventoryNote: optionalText(formData.inventoryNote),
      nNumber: optionalText(formData.nNumber),
      nNumberActiveDate: optionalText(formData.nNumberActiveDate),
      remoteId: optionalText(formData.remoteId),
      remoteBroadcastModule: optionalText(formData.remoteBroadcastModule),
    };
  }

  /** PUT the writable fields, then the status through its own route when it changed (the drone PUT ignores it). */
  private async saveChanges(droneId: string): Promise<void> {
    await firstValueFrom(this.droneService.updateDrone(droneId, this.toRequest()));
    const status: DroneStatus = this.droneForm.value.status;
    if (status !== 'leased' && canChangeDroneStatus(this.loadedStatus, status)) {
      // Back to service from maintenance also restores a saved lease (see DroneService.returnToService)
      if (status === 'assigned' && this.loadedStatus === 'maintenance') {
        await this.droneService.returnToService(droneId);
      } else {
        await firstValueFrom(this.droneService.updateStatus(droneId, status));
      }
      this.loadedStatus = status;
    }

    this.successMessage = 'Drone updated successfully';

    // Navigate back to detail page after short delay
    setTimeout(() => {
      this.router.navigate(['/drones', droneId, 'details']);
    }, 1500);
  }

  private async createAndShowSuccess(): Promise<void> {
    const formData = this.droneForm.value;
    await firstValueFrom(this.droneService.createDrone({ ...this.toRequest(), status: formData.status }));

    // Get manufacturer and model names for success page
    const manufacturer = this.manufacturers.find(m => m.id === formData.manufacturerId);
    const model = this.availableModels.find(m => m.id === formData.modelId);

    this.router.navigate(['/success'], {
      state: {
        entities: [
          { type: 'drone', action: 'created', name: formData.name },
        ],
        message: 'Drone registered successfully',
        details: {
          'Manufacturer': manufacturer?.name || 'Unknown',
          'Model': model?.modelName || 'Unknown',
          'Serial Number': formData.serialNumber,
          'N-Number': formData.nNumber || 'Not registered',
          'Status': this.getStatusLabel(formData.status),
          'Inventory Note': (formData.inventoryNote || '').trim() || '—'
        },
        createAnotherRoute: '/drones/create'
      }
    });
  }

  cancel(): void {
    if (this.isEditMode && this.droneId) {
      this.router.navigate(['/drones', this.droneId, 'details']);
    } else {
      this.router.navigate(['/drones']);
    }
  }

  get pageTitle(): string {
    return this.isEditMode ? 'Edit Drone' : 'Add New Drone';
  }

  get submitButtonText(): string {
    if (this.loading) {
      return this.isEditMode ? 'Saving...' : 'Creating...';
    }
    return this.isEditMode ? 'Save Changes' : 'Create Drone';
  }

  /** Whether the status select offers `target` for this drone (create allows any listed status). */
  canSelectStatus(target: string): boolean {
    return !this.isEditMode || target === this.loadedStatus
      || canChangeDroneStatus(this.loadedStatus, target as DroneStatus);
  }

  /**
   * Get display label for status
   */
  private getStatusLabel(status: string): string {
    const found = this.statuses.find(s => s.value === status);
    return found?.label || status;
  }
}
